/**
 * Single source of truth for legal and contact details.
 *
 * Simino Social is a product of SIMINO LABTEST PRIVATE LIMITED, built with SmartNetra,
 * the company's AI product builder. The registered entity and address below must match
 * the company's public records exactly — Meta and Google reviewers cross-check them
 * during App Review and business verification. Change them in one place only.
 */

export const COMPANY = {
  /** Product / trading name shown to users. */
  product: "Simino Social",
  /** Registered legal entity that operates the product. */
  legalName: "SIMINO LABTEST PRIVATE LIMITED",
  /**
   * Canonical public origin. This is now the product's own registrable domain, so it is
   * both Meta's App Domain and Google's Authorised domain — no parent-domain split any
   * more. Changing it again means re-running App Review and re-verifying ownership in
   * Google Search Console.
   */
  site: "https://siminosocial.com",

  address: {
    line1: "House No-3, Kokila garden phase 1",
    line2: "Pokhariput",
    city: "Bhubaneswar",
    state: "Odisha",
    postalCode: "751020",
    country: "India",
    /** Flat form for schema.org and legal page footers. */
    oneLine:
      "House No-3, Kokila garden phase 1, Pokhariput, Bhubaneswar, Odisha, India 751020",
  },

  /**
   * Meta tests the deletion path in this mailbox during App Review and Google lists it
   * on the consent screen, so it must be live and monitored before either submission.
   */
  supportEmail: "support@siminosocial.com",
  privacyEmail: "support@siminosocial.com",
  /** The single published contact number; it must ring at the registered address. */
  phone: "+91 9776 919 100",
  /**
   * GSTIN of the registered entity. Indian invoicing rules require it on the tax
   * invoices we issue, and it is the number buyers reconcile against, so it is
   * published alongside the address rather than only shown at checkout.
   */
  gstin: "21ABOCS2500F1ZT",

  /**
   * The AI product builder Simino Social is made with. SmartNetra is SIMINO LABTEST
   * PRIVATE LIMITED's own tooling, not a third-party vendor, so crediting it does not
   * create a processor relationship that the privacy policy would have to disclose.
   */
  studio: {
    name: "SmartNetra",
    tagline: "The AI Product Builder",
    url: "https://smartnetra.com",
  },

  /**
   * Grievance Officer, as required of an Indian intermediary by Rule 3(2)(a) of the
   * Information Technology (Intermediary Guidelines and Digital Media Ethics Code)
   * Rules, 2021. The rule requires the officer's NAME and contact to be published on
   * the site, not just the role and the mailbox. Changing who holds the post is a
   * change here, in `app/mobile-app/lib/src/compliance/company.dart`, and on the
   * published Terms page — the three are cross-checked.
   */
  grievance: {
    officerName: "Gyana Ranjan Pradhan",
    /**
     * A dedicated mailbox, not the shared support address: Rule 3(2)(a) complaints
     * carry their own clock (acknowledge in 24h, dispose in 15 days) and must not
     * be lost in the general queue. It has to be live and monitored before launch.
     */
    email: "grievance@siminosocial.com",
    /** Rule 3(2)(a): acknowledge within 24 hours, dispose of within 15 days. */
    acknowledgeHours: 24,
    resolveDays: 15,
  },

  /**
   * The company's own presence on the platforms Simino Social publishes to. Reviewers
   * follow these to confirm the app belongs to a real, active business, so they must
   * point at live pages owned by the entity above.
   */
  social: {
    facebook: "https://www.facebook.com/profile.php?id=61593592538124",
  },

  jurisdiction: "Bhubaneswar, Odisha, India",
  effectiveDate: "22 August 2026",
  lastUpdated: "25 August 2026",
} as const;

/** Platform links the Meta and YouTube API terms require us to surface. */
export const PLATFORM_LINKS = {
  metaTerms: "https://www.facebook.com/terms.php",
  metaPrivacy: "https://www.facebook.com/privacy/policy/",
  metaAppSettings: "https://www.facebook.com/settings?tab=business_tools",
  instagramTerms: "https://help.instagram.com/581066165581870",
  youtubeTerms: "https://www.youtube.com/t/terms",
  googlePrivacy: "https://policies.google.com/privacy",
  googlePermissions: "https://myaccount.google.com/permissions",
  googleLimitedUse:
    "https://developers.google.com/terms/api-services-user-data-policy",
} as const;
